import { createBrowserRouter, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { RootLayout } from '@/app/layouts/RootLayout';
import { LoadingState } from '@/components/states/LoadingState';
import { ErrorBoundaryRoute } from '@/app/ErrorBoundaryRoute';
import { RequireAuth } from '@/auth/RequireAuth';

const HomePage = lazy(() => import('@/pages/HomePage'));
const TicketPage = lazy(() => import('@/pages/tickets/TicketPage'));
const EventPage = lazy(() => import('@/pages/events/EventPage'));
const EventMenuPage = lazy(() => import('@/pages/events/EventMenuPage'));
const GroupPage = lazy(() => import('@/pages/groups/GroupPage'));
const JoinPage = lazy(() => import('@/pages/JoinPage'));
const PurchasePage = lazy(() => import('@/pages/purchase/PurchasePage'));
const PaymentReturnPage = lazy(() => import('@/pages/purchase/PaymentReturnPage'));
const ScanPage = lazy(() => import('@/pages/scan/ScanPage'));
const ActivateScannerPage = lazy(() => import('@/pages/scan/ActivateScannerPage'));
const PanelLayout = lazy(() => import('@/pages/panel/PanelLayout'));
const PanelDashboardPage = lazy(() => import('@/pages/panel/PanelDashboardPage'));
const PanelEventsPage = lazy(() => import('@/pages/panel/PanelEventsPage'));
const PanelEventDetailPage = lazy(() => import('@/pages/panel/PanelEventDetailPage'));
const PanelEventSettingsPage = lazy(() => import('@/pages/panel/PanelEventSettingsPage'));
const PanelStaffPage = lazy(() => import('@/pages/panel/PanelStaffPage'));
const PanelPollsPage = lazy(() => import('@/pages/panel/PanelPollsPage'));
const PanelOrdersPage = lazy(() => import('@/pages/panel/PanelOrdersPage'));
const PanelScanLogPage = lazy(() => import('@/pages/panel/PanelScanLogPage'));
const PanelMySalesPage = lazy(() => import('@/pages/panel/PanelMySalesPage'));
const RpLandingPage = lazy(() => import('@/pages/RpLandingPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

const withSuspense = (Element: React.ComponentType) => (
  <Suspense fallback={<LoadingState />}>
    <Element />
  </Suspense>
);

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <ErrorBoundaryRoute />,
    children: [
      { index: true, element: withSuspense(HomePage) },

      // Login is owned entirely by the RSV ID SDK (`sdk.login()`); no
      // /login page lives in this SPA. Old bookmarks fall back to home.
      { path: 'login', element: <Navigate to="/" replace /> },

      // Public ticket display: /t/:secret  (replaces _epass.php)
      { path: 't/:secret', element: withSuspense(TicketPage) },

      // Public event: /e/:eventCode  (replaces _event.php)
      { path: 'e/:eventCode', element: withSuspense(EventPage) },
      { path: 'e/:eventCode/menu', element: withSuspense(EventMenuPage) },

      // Group: /g/:groupCode  (replaces _group.php)
      { path: 'g/:groupCode', element: withSuspense(GroupPage) },

      // Public RP attribution landing: /rp/:rpCode/:eventCode
      { path: 'rp/:rpCode/:eventCode', element: withSuspense(RpLandingPage) },

      // Invite redemption: /join/:inviteCode  (replaces _join.php)
      { path: 'join/:inviteCode', element: withSuspense(JoinPage) },

      // Purchase flow: /buy/:eventCode  (replaces purchase/index.php)
      { path: 'buy/:eventCode', element: withSuspense(PurchasePage) },
      { path: 'buy/:eventCode/return', element: withSuspense(PaymentReturnPage) },

      // Scan flow: requires auth + event scope.
      {
        path: 'scan',
        element: (
          <RequireAuth>
            <Suspense fallback={<LoadingState />}>
              <ScanPage />
            </Suspense>
          </RequireAuth>
        ),
      },
      { path: 'scan/activate', element: withSuspense(ActivateScannerPage) },

      // Admin panel — auth required, uses nested layout.
      {
        path: 'panel',
        element: (
          <RequireAuth>
            <Suspense fallback={<LoadingState />}>
              <PanelLayout />
            </Suspense>
          </RequireAuth>
        ),
        children: [
          { index: true, element: withSuspense(PanelDashboardPage) },
          { path: 'events', element: withSuspense(PanelEventsPage) },
          { path: 'events/:eventCode', element: withSuspense(PanelEventDetailPage) },
          { path: 'events/:eventCode/settings', element: withSuspense(PanelEventSettingsPage) },
          { path: 'events/:eventCode/staff', element: withSuspense(PanelStaffPage) },
          { path: 'events/:eventCode/polls', element: withSuspense(PanelPollsPage) },
          { path: 'events/:eventCode/orders', element: withSuspense(PanelOrdersPage) },
          { path: 'events/:eventCode/scan-log', element: withSuspense(PanelScanLogPage) },
          { path: 'me/sales', element: withSuspense(PanelMySalesPage) },
        ],
      },

      // Legacy alias: /epass?ev=&s= → /t/:secret
      { path: 'epass', element: <Navigate to="/" replace /> },

      { path: '*', element: withSuspense(NotFoundPage) },
    ],
  },
]);
